import { useEffect, useRef, useCallback } from "react"
import { useInView, useMotionValue, useSpring } from "motion/react"

type AnimatedNumberProps = {
  value: number | null
  format?: "currency" | "number" | "percent" | "decimal"
  compact?: boolean
  className?: string
}

export default function AnimatedNumber({
  value,
  format,
  compact = false,
  className,
}: AnimatedNumberProps) {
  const ref = useRef<HTMLSpanElement>(null)

  // Determine the display value based on format
  const isValidValue = value !== null && Number.isFinite(value)
  const percentAdjusted = format === "percent" ? (value ?? 0) * 100 : value
  const displayValue = isValidValue ? (percentAdjusted ?? 0) : 0

  const motionValue = useMotionValue(0)
  // Fast spring that completes in ~1 second
  const springValue = useSpring(motionValue, {
    stiffness: 400,
    damping: 40,
    mass: 1,
  })
  const isInView = useInView(ref, { once: true, margin: "0px" })

  // Format the animated value for display
  const formatAnimatedValue = useCallback(
    (latest: number): string => {
      if (!Number.isFinite(latest)) return "0.0"

      switch (format) {
        case "currency":
          return new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: "USD",
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
            notation: compact ? "compact" : "standard",
            compactDisplay: compact ? "short" : undefined,
          }).format(latest)
        case "percent":
          return `${latest.toFixed(1)}%`
        case "decimal":
          return latest.toFixed(2)
        case "number":
        default:
          return new Intl.NumberFormat("en-US", {
            notation: compact ? "compact" : "standard",
            compactDisplay: compact ? "short" : undefined,
            minimumFractionDigits: 1,
            maximumFractionDigits: 1,
          }).format(latest)
      }
    },
    [format, compact],
  )

  // Initialize display
  useEffect(() => {
    if (ref.current) {
      ref.current.textContent = formatAnimatedValue(0)
    }
  }, [formatAnimatedValue])

  // Start animation when in view
  useEffect(() => {
    if (isInView && value !== null && Number.isFinite(value)) {
      motionValue.set(displayValue)
    }
  }, [isInView, value, displayValue, motionValue])

  // Subscribe to spring value changes
  useEffect(() => {
    const unsubscribe = springValue.on("change", latest => {
      if (ref.current) {
        ref.current.textContent = formatAnimatedValue(latest)
      }
    })
    return () => unsubscribe()
  }, [springValue, formatAnimatedValue])

  if (value === null || !Number.isFinite(value)) {
    return <span className={className}>—</span>
  }

  return <span className={className} ref={ref} />
}
